// BillSplitPanel — web mirror of mobile BillSplitPanel.jsx.
// Flatmate spaces only: pulls /api/spaces/settlements and renders
// per-member paid + net + Splitwise-style minimum-transaction plan.
//
// V1 assumption (documented in backend): the user who uploaded an
// invoice is the payer. Manual paid_by overrides are P1-8 backlog.

import React, { useCallback, useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, RotateCw, Wallet } from "lucide-react";
import { api } from "@/lib/api";

const INR = (n) =>
  `₹${Math.abs(Number(n) || 0).toLocaleString("en-IN")}`;

export default function BillSplitPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const { data: res } = await api.get("/spaces/settlements");
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load settlements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="cartiq-card" data-testid="bill-split-loading">
        <div className="flex items-center gap-3 text-sm text-[#64748B]">
          <div className="w-2 h-2 rounded-full bg-[#3EB489] animate-pulse" />
          Crunching settle-up…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="cartiq-card" data-testid="bill-split-error">
        <div className="text-sm text-[#E11D48] font-semibold">{error}</div>
        <button
          type="button"
          onClick={load}
          className="mt-2 inline-flex items-center gap-1.5 text-[#3EB489] hover:text-[#2D8C6E] text-[11px] font-bold uppercase tracking-[0.16em]"
        >
          <RotateCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  const paid = data.paid || [];
  const transfers = data.transfers || [];
  const noOne = paid.every((p) => p.paid === 0);

  return (
    <div className="cartiq-card" data-testid="bill-split-panel">
      <div className="flex items-center gap-2">
        <Wallet className="w-4 h-4 text-[#2D8C6E]" />
        <h3 className="font-display uppercase text-base text-[#0F172A]">
          Bill Split · {data.label}
        </h3>
      </div>

      {/* Top-line summary */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
            Total Paid
          </div>
          <div
            className="mt-1 text-2xl font-extrabold text-[#0F172A] font-mono-num"
            data-testid="bill-split-total"
          >
            {INR(data.totalPaid)}
          </div>
        </div>
        <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
            Fair Share / Head
          </div>
          <div
            className="mt-1 text-2xl font-extrabold text-[#2D8C6E] font-mono-num"
            data-testid="bill-split-fairshare"
          >
            {INR(data.fairShare)}
          </div>
        </div>
      </div>

      {/* Per-member rows */}
      <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
        Who Paid What
      </div>
      <ul className="mt-2 space-y-0">
        {paid.map((p) => {
          const isCreditor = p.net > 0;
          const isDebtor = p.net < 0;
          return (
            <li
              key={p.user_id}
              className="flex items-center gap-3 py-2.5 border-b border-[#E2E8F0] last:border-b-0"
              data-testid={`bill-split-paid-${p.user_id}`}
            >
              <div className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#3EB489] font-extrabold text-sm">
                {(p.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#334155] truncate">
                  {p.name}
                </div>
                <div className="text-[11px] text-[#64748B]">
                  Paid {INR(p.paid)} · Share {INR(p.share)}
                </div>
              </div>
              <div className="text-right font-mono-num font-extrabold text-sm">
                {isCreditor ? (
                  <span className="text-[#2D8C6E]">+ {INR(p.net)}</span>
                ) : isDebtor ? (
                  <span className="text-[#E11D48]">− {INR(p.net)}</span>
                ) : (
                  <span className="text-[#94A3B8] text-[11px] uppercase tracking-widest">
                    Settled
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Settle-up plan */}
      <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
        Settle-Up Plan
      </div>
      {noOne ? (
        <p className="mt-3 text-sm text-[#64748B] text-center py-4 italic">
          No spend recorded this month yet. Upload a receipt to start the tally.
        </p>
      ) : transfers.length === 0 ? (
        <div
          className="mt-2 flex items-center gap-2 p-3 rounded-lg bg-[#3EB489]/10 border border-[#3EB489]/25"
          data-testid="bill-split-all-settled"
        >
          <CheckCircle2 className="w-4 h-4 text-[#2D8C6E]" />
          <span className="text-sm font-bold text-[#2D8C6E]">
            Everyone's even. Nice.
          </span>
        </div>
      ) : (
        <ul className="mt-2 space-y-2">
          {transfers.map((t, i) => (
            <li
              key={i}
              className="flex items-center gap-3 px-3 py-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]"
              data-testid={`bill-split-transfer-${i}`}
            >
              <div className="flex-1 text-sm font-bold text-[#334155] truncate">
                {t.from_name}
              </div>
              <div className="flex items-center gap-2 text-[#2D8C6E]">
                <ArrowRight className="w-4 h-4" />
                <span className="font-extrabold font-mono-num text-sm">
                  {INR(t.amount)}
                </span>
              </div>
              <div className="flex-1 text-sm font-bold text-[#334155] text-right truncate">
                {t.to_name}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-[10px] italic text-[#94A3B8] text-center">
        Uploader = payer. Manual overrides coming soon.
      </p>
    </div>
  );
}
